<script lang="ts" setup>
import { computed } from 'vue'
import type { ServiceHistory } from '@/types'
import RunTransactionExplorer from './RunTransactionExplorer.vue'

const props = defineProps<{
  isVisible: boolean
  nodeData: {
    label: string
    serviceType?: string
    status?: string
    runData?: ServiceHistory
  } | null
  nodeId: string | null
  runId: string
  /** The run's services, for naming the services a transaction refers to. Ids are shown without them. */
  services?: ServiceHistory[]
}>()

const emit = defineEmits<{
  close: []
}>()

const serviceNames = computed<Record<string, string>>(() =>
  Object.fromEntries((props.services ?? []).map((s) => [s.serviceId, s.serviceName]))
)

const serviceIcon = computed(() => {
  if (!props.nodeData) return 'bi-gear'

  const type = props.nodeData.serviceType || props.nodeData.label.toLowerCase()

  if (type.includes('email') || type.includes('send email')) return 'bi-envelope'
  if (type.includes('api') || type.includes('trigger')) return 'bi-lightning'
  if (type.includes('delay') || type.includes('wait')) return 'bi-clock'
  if (type.includes('condition') || type.includes('if')) return 'bi-question-diamond'
  if (type.includes('database') || type.includes('data')) return 'bi-database'
  if (type.includes('webhook')) return 'bi-link-45deg'
  if (type.includes('transform') || type.includes('format')) return 'bi-arrow-left-right'
  if (type.includes('notification') || type.includes('alert')) return 'bi-bell'
  if (type.includes('log') || type.includes('audit')) return 'bi-journal-text'
  if (type.includes('file') || type.includes('upload')) return 'bi-file-earmark'

  return 'bi-gear'
})

const statusBadgeClass = computed(() => {
  if (!props.nodeData?.status) return 'bg-secondary'

  switch (props.nodeData.status) {
    case 'success':
    case 'completed':
    case 'Succeeded':
      return 'bg-success'
    case 'error':
    case 'failed':
    case 'Failed':
      return 'bg-danger'
    case 'warning':
    case 'Warning':
      return 'bg-warning'
    case 'pending':
    case 'Pending':
      return 'bg-secondary'
    case 'skipped':
      return 'bg-secondary'
    default:
      return 'bg-secondary'
  }
})

function handleClose() {
  emit('close')
}

function handleOverlayClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    handleClose()
  }
}
</script>

<template>
  <div
    v-if="isVisible"
    class="service-details-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
    @click="handleOverlayClick"
  >
    <div class="service-details-panel card shadow-lg" @click.stop>
      <div class="card-header d-flex align-items-center justify-content-between gap-2">
        <div class="d-flex align-items-center gap-3 min-w-0">
          <div
            class="service-icon bg-body-tertiary border d-flex align-items-center justify-content-center"
          >
            <i :class="serviceIcon" class="run-service-detail-icon"></i>
          </div>
          <h2 class="mb-0 text-truncate">{{ nodeData?.label || 'Service Details' }}</h2>
          <span v-if="nodeId" class="badge bg-body-secondary text-body-secondary font-monospace fw-normal">
            {{ nodeId }}
          </span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span v-if="nodeData?.status" class="badge" :class="statusBadgeClass">
            {{ nodeData.status }}
          </span>
          <button type="button" class="btn-close" @click="handleClose"></button>
        </div>
      </div>

      <div class="card-body">
        <RunTransactionExplorer
          v-if="nodeId && runId"
          :run-id="runId"
          :service-id="nodeId"
          :total="nodeData?.runData?.total ?? 0"
          :service-names="serviceNames"
        />
        <div v-else class="text-muted text-center py-5">
          <i class="bi bi-info-circle display-6 d-block mb-2"></i>
          <p class="mb-0">Run this workflow to see its transactions.</p>
        </div>
      </div>

      <div class="card-footer d-flex justify-content-end">
        <button type="button" class="bb-btn-cancel" @click="handleClose">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.service-details-overlay {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1055;
  backdrop-filter: blur(2px);
}

.service-details-panel {
  max-width: 1100px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.service-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  font-size: 18px;
  flex-shrink: 0;
}

.run-service-detail-icon {
  color: var(--bs-secondary-color);
}

[data-bs-theme='dark'] .service-icon {
  background-color: var(--bs-secondary-bg) !important;
  border-color: var(--bs-secondary-border-subtle) !important;
}

[data-bs-theme='dark'] .run-service-detail-icon {
  color: var(--bs-secondary-color);
}
</style>
